/**
 * Lifestyle connectors · shared shapes.
 *
 * Every connector exports an instance of `Connector<TPayload>`. The host app
 * stores connection state via `state` + writes a `ConsentReceipt` on connect.
 *
 * Two execution modes:
 *   - mock  -> realistic seeded data (default; no auth, no network)
 *   - live  -> OAuth/fetch (requires creds + user consent)
 */

export type ConnectorId =
  | 'gmail-orders'
  | 'gcalendar'
  | 'gcontacts'
  | 'apple-contacts'
  | 'wa-chat-export'
  | 'zomato-swiggy'
  | 'uber-ola'
  | 'mmt-travel'
  | 'subscriptions'
  | 'linkedin'
  | 'gphotos'
  | 'sms-android';

export type ConnectorPurpose =
  | 'wardrobe.auto_import'
  | 'wardrobe.wishlist_seed'
  | 'relationships.calendar_birthdays'
  | 'relationships.contacts_seed'
  | 'relationships.interaction_balance'
  | 'relationships.work_circle_seed'
  | 'relationships.travel_partners'
  | 'lifestyle.context_signal'
  | 'wasiyat.subscription_map'
  | 'finance.txn_signal';

export type Mode = 'mock' | 'live';
export type Status = 'idle' | 'connecting' | 'connected' | 'syncing' | 'error';

export interface ConnectorState {
  readonly id: ConnectorId;
  readonly status: Status;
  readonly mode: Mode;
  readonly last_sync_ts?: number;
  readonly items_imported?: number;
  readonly error?: string;
  readonly token_hash?: string;
}

export interface ConsentReceipt {
  readonly connector_id: ConnectorId;
  readonly purposes: readonly ConnectorPurpose[];
  readonly scopes: readonly string[];
  readonly granted_at: number;
  readonly user_passkey_hash: string;
  readonly retention_days: number;
  readonly app: 'almari' | 'dial' | 'hisaab' | 'khazaana' | 'tankhwah' | 'aramgah' | 'sehat' | 'wasiyat';
}

export interface ConnectMeta {
  readonly id: ConnectorId;
  readonly title_en: string;
  readonly title_hi?: string;
  readonly description: string;
  readonly purposes: readonly ConnectorPurpose[];
  readonly scopes: readonly string[];
  readonly retention_days: number;
  readonly mode_default: Mode;
  readonly v0_status: 'live' | 'mock_only' | 'file_drop' | 'mobile_only';
}

export interface Connector<TPayload> {
  readonly meta: ConnectMeta;
  readonly state: ConnectorState;

  connect(opts: { app: ConsentReceipt['app']; passkey_hash: string; mode?: Mode }): Promise<ConsentReceipt>;
  disconnect(): Promise<void>;
  sync(opts?: { since_ts?: number }): Promise<TPayload>;
}

/** Universal money. India only for now. */
export interface Paise { readonly paise: number; }
export const paise = (n: number): Paise => ({ paise: Math.round(n) });

/** Where the import came from -- the audit trail anchor. */
export interface Provenance {
  readonly connector: ConnectorId;
  readonly source_id?: string;
  readonly fetched_at: number;
  readonly raw_hash?: string;
}

export interface WardrobeOrderItem {
  readonly merchant: 'amazon' | 'flipkart' | 'myntra' | 'ajio' | 'nykaa' | 'tata-cliq' | 'meesho' | 'other';
  readonly order_id: string;
  readonly order_date: string;
  readonly total: Paise;
  readonly items: ReadonlyArray<{
    readonly title: string;
    readonly brand?: string;
    readonly category?: string;
    readonly qty: number;
    readonly unit_price: Paise;
    readonly photo_url?: string;
    readonly product_url?: string;
    readonly size?: string;
    readonly color?: string;
  }>;
  readonly provenance: Provenance;
}

export interface ContactSeed {
  readonly external_id: string;
  readonly name: string;
  readonly phones: readonly string[];
  readonly emails: readonly string[];
  readonly photo_url?: string;
  readonly birthday?: string;
  readonly anniversary?: string;
  readonly relation_hint?: string;
  readonly source: 'gcontacts' | 'apple-contacts' | 'linkedin' | 'wa-chat';
  readonly provenance: Provenance;
}

export interface CalendarEvent {
  readonly external_id: string;
  readonly title: string;
  readonly start: string;
  readonly end?: string;
  readonly recurrence?: 'yearly' | 'monthly' | 'weekly';
  readonly kind: 'birthday' | 'anniversary' | 'event';
  readonly attendees?: readonly string[];
  readonly provenance: Provenance;
}

export interface InteractionStat {
  readonly contact_key: string;
  readonly month: string;
  readonly messages_out: number;
  readonly messages_in: number;
  readonly calls_out: number;
  readonly calls_in: number;
  readonly gifts_mentioned: number;
  readonly longest_gap_days: number;
  readonly provenance: Provenance;
}

export interface Subscription {
  readonly merchant: string;
  readonly category: 'streaming' | 'saas' | 'cloud' | 'news' | 'fitness' | 'utility' | 'other';
  readonly amount_monthly: Paise;
  readonly billing_period: 'monthly' | 'quarterly' | 'yearly';
  readonly next_renewal?: string;
  readonly auto_renew: boolean;
  readonly first_seen: string;
  readonly last_seen: string;
  readonly count: number;
  readonly provenance: Provenance;
}

export interface TripRecord {
  readonly merchant: 'uber' | 'ola' | 'rapido' | 'blusmart';
  readonly external_id: string;
  readonly start: string;
  readonly origin: string;
  readonly destination: string;
  readonly fare: Paise;
  readonly tip?: Paise;
  readonly distance_km?: number;
  readonly passengers?: readonly string[];
  readonly provenance: Provenance;
}

export interface TravelBooking {
  readonly merchant: 'mmt' | 'yatra' | 'irctc' | 'easemytrip' | 'goibibo';
  readonly pnr: string;
  readonly kind: 'flight' | 'train' | 'bus' | 'hotel' | 'cab';
  readonly start: string;
  readonly end?: string;
  readonly origin?: string;
  readonly destination: string;
  readonly cost: Paise;
  readonly travellers: readonly string[];
  readonly provenance: Provenance;
}

export interface FoodOrder {
  readonly merchant: 'zomato' | 'swiggy' | 'eatsure' | 'dineout';
  readonly order_id: string;
  readonly placed_at: string;
  readonly restaurant?: string;
  readonly total: Paise;
  readonly items_count: number;
  readonly delivery_window?: 'morning' | 'lunch' | 'evening' | 'dinner' | 'late_night';
  readonly provenance: Provenance;
}

export interface WorkProfile {
  readonly person_key: string;
  readonly company: string;
  readonly title: string;
  readonly start: string;
  readonly end?: string;
  readonly location?: string;
  readonly seniority?: 'junior' | 'mid' | 'senior' | 'lead' | 'director' | 'vp' | 'cxo';
  readonly source: 'linkedin';
  readonly provenance: Provenance;
}

export interface PhotoSlim {
  readonly external_id: string;
  readonly fetched_url: string;
  readonly captured_at?: string;
  readonly mime: string;
  readonly bytes_estimate?: number;
  readonly camera_model?: string;
  readonly provenance: Provenance;
}

export interface SmsTxn {
  readonly raw_sender: string;
  readonly kind: 'upi' | 'bank_debit' | 'bank_credit' | 'card_swipe' | 'otp' | 'other';
  readonly amount?: Paise;
  readonly merchant?: string;
  readonly account_tail?: string;
  readonly received_at: string;
  readonly provenance: Provenance;
}
